/**
 * The persistent take-down notice (listing-marketing-hub BR-15c): whenever a listing is not active
 * but still has posted records — however it stopped being active (Pause, Close, approving an
 * applicant, a paid period ending) — on the listing and on its leads' detail pages. It clears when
 * the last posted record is marked removed or deleted.
 */
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import type { ExternalListingPostDto } from '../../lib/external-listing-posts.api';
import type { Listing } from '../../lib/leasing.api';
import { postedPosts, siteNames } from '../../lib/listing-marketing';
import { buttonVariants } from '../ui/button-variants';
import { LISTING_STATUS_LABELS } from './listing-status';

interface TakeDownNoticeProps {
  listingId: string;
  status: Listing['status'];
  posts: ExternalListingPostDto[] | undefined;
  /** On the listing's own page the action is a hash link to its Marketing tab. */
  onListingPage: boolean;
}

export function TakeDownNotice({ listingId, status, posts, onListingPage }: TakeDownNoticeProps) {
  const posted = postedPosts(posts);
  if (status === 'active' || posted.length === 0) return null;
  const n = posted.length;
  const state = status === 'draft' ? 'unpublished' : LISTING_STATUS_LABELS[status].toLowerCase();
  const actionClass = buttonVariants({ className: 'min-h-[44px] shrink-0' });

  return (
    <section
      role="alert"
      data-testid="listing-takedown-notice"
      className="flex flex-col gap-3 rounded-lg border border-warning/25 bg-warning/10 p-4 text-sm sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="flex min-w-0 items-start gap-3">
        <AlertTriangle aria-hidden="true" className="mt-0.5 h-[18px] w-[18px] shrink-0 text-warning" />
        <p className="text-foreground">
          <span className="font-semibold text-warning">
            Listing {state}. Your {n === 1 ? 'post is' : `${n} posts are`} still live.
          </span>{' '}
          Delete {n === 1 ? 'it' : 'them'} on {siteNames(posted)}, then mark {n === 1 ? 'it' : 'them'} removed.
        </p>
      </div>
      {onListingPage ? (
        <a href="#marketing" className={actionClass} data-testid="listing-takedown-notice-action">
          Take down posts
        </a>
      ) : (
        <Link
          to={{ pathname: `/leasing/listings/${listingId}`, hash: '#marketing' }}
          className={actionClass}
          data-testid="listing-takedown-notice-action"
        >
          Take down posts
        </Link>
      )}
    </section>
  );
}
